/**
 * @file containerService.js
 * @description APIs for container related requests.
 **/

import { contentHttp, fileUploadHttp } from './httpService';

/**
 * Retrieve the tenant ID from local storage.
 * @returns {string|null} The tenant ID or null if not found.
 */
const getTenantId = () => +window.localStorage.getItem('tenantId');

export const createContainer = async payload => {
    let tenantID = getTenantId();
    try {
        const response = await contentHttp.post(
            `/v2/content/tenant/${tenantID}/containers`,
            payload,
        );
        return response?.data || null;
    } catch (error) {
        console.error('Error fetching current user data:', error);
        throw error;
    }
};

export const uploadFile = async (file, { onUploadProgress, signal } = {}) => {
    const formData = new FormData();
    formData.append('file', file);

    let tenantID = getTenantId();

    const headers = {
        'Content-Type': 'multipart/form-data',
        Accept: '*/*',
    };

    try {
        const response = await fileUploadHttp.post(
            `/v2/content/tenant/${tenantID}/containers/file`,
            formData,
            {
                headers,
                onUploadProgress,
                signal,
            },
        );
        return response?.data || null;
    } catch (error) {
        console.error('Error uploading file:', error);
        throw error;
    }
};

//100MB per chunk to match the S3 multipart chunk size on the backend
const CHUNK_SIZE_BYTES = 100 * 1024 * 1024;

/**
 * Uploads a file to the backend's chunked endpoints (initiate/chunk/complete)
 * instead of one single-shot multipart request. Same signature and return
 * shape as uploadFile, so it's a drop-in replacement once the backend
 * supports the chunked contract.
 */
export const uploadFileChunked = async (
    file,
    { onUploadProgress, signal, chunkSizeBytes = CHUNK_SIZE_BYTES } = {},
) => {
    const tenantID = getTenantId();
    const totalParts = Math.max(1, Math.ceil(file.size / chunkSizeBytes));

    const { data: initiateResponse } = await fileUploadHttp.post(
        `/v2/content/tenant/${tenantID}/containers/file/initiate`,
        {
            fileName: file.name,
            contentType: file.type,
            totalSize: file.size,
        },
        { signal },
    );
    const { uploadId } = initiateResponse;

    for (let partNumber = 0; partNumber < totalParts; partNumber += 1) {
        const start = partNumber * chunkSizeBytes;
        const end = Math.min(start + chunkSizeBytes, file.size);
        const chunk = file.slice(start, end);
        const bytesUploadedBeforeThisChunk = start;

        await fileUploadHttp.post(
            `/v2/content/tenant/${tenantID}/containers/file/${uploadId}/chunk`,
            chunk,
            {
                headers: { 'Content-Type': 'application/octet-stream' },
                params: { offset: start },
                signal,
                onUploadProgress: onUploadProgress
                    ? chunkProgressEvent =>
                          onUploadProgress({
                              loaded:
                                  bytesUploadedBeforeThisChunk +
                                  chunkProgressEvent.loaded,
                              total: file.size,
                          })
                    : undefined,
            },
        );
    }

    const response = await fileUploadHttp.post(
        `/v2/content/tenant/${tenantID}/containers/file/${uploadId}/complete`,
        null,
        { signal },
    );
    return response?.data || null;
};

export const updateFile = async (file, { onUploadProgress, signal } = {}) => {
    const formData = new FormData();
    formData.append('file', file);

    let tenantID = getTenantId();

    const headers = {
        'Content-Type': 'multipart/form-data',
        Accept: '*/*',
    };

    try {
        const response = await fileUploadHttp.put(
            `/v2/content/tenant/${tenantID}/containers/file`,
            formData,
            {
                headers,
                onUploadProgress,
                signal,
            },
        );
        return response?.data || null;
    } catch (error) {
        console.error('Error updating file:', error);
        throw error;
    }
};

/**
 * Fetches all containers for a given tenant ID.
 *
 * @param {number} tenantId The ID of the client.
 * @returns {Promise<Object>} A promise that resolves to the list of containers.
 */
export const queryContainers = async (tenantId = null) => {
    if (!tenantId) tenantId = getTenantId();
    try {
        const response = await contentHttp.get(
            `/v2/content/tenant/${tenantId}/containers/all`,
        );
        return response?.data || null;
    } catch (error) {
        console.error(
            `Error fetching containers with tenantId ${tenantId}:`,
            error,
        );
        throw error;
    }
};

/**
 *
 * @returns {} - A promise that resolve to the user onboarding details
 */

/**
 * Fetches a container by ID.
 *
 * @param {number} id The ID of the template.
 * @returns {Promise<Object>} A promise that resolves to the template data.
 */
export const readContainer = async (id, tenantId = null) => {
    if (tenantId === null) {
        tenantId = localStorage.getItem('tenantId') || 0;
    }
    try {
        const response = await contentHttp.get(
            `/v2/content/tenant/${tenantId}/containers/single/${id}`,
        );
        return response.data.viewModels[0];
    } catch (error) {
        console.error(`Error fetching container with ID ${id}:`, error);
        throw error;
    }
};

/**
 * Updates a container.
 *
 * @param {Object} updatedContainer The updated data for the container.
 * @returns {Promise<Object>} A promise that resolves to the updated container data.
 */
export const updateContainer = async updatedContainer => {
    try {
        const tenantID = localStorage.getItem('tenantId') || 0;
        const response = await contentHttp.put(
            `/v2/content/tenant/${tenantID}/containers`,
            updatedContainer,
        );
        return response.data || null;
    } catch (error) {
        console.error(
            `Error updating container with ID ${updatedContainer.id}:`,
            error,
        );
        throw error;
    }
};

/**
 *
 * @returns {} - A promise that resolve to the user onboarding details
 */

export const deleteContainer = async id => {
    try {
        const tenantID = localStorage.getItem('tenantId') || 0;
        const response = await contentHttp.delete(
            `/v2/content/tenant/${tenantID}/containers/${id}`,
        );
        return response;
    } catch (error) {
        console.error(`Error fetching ontainer with ID ${id}:`, error);
        throw error;
    }
};

/**
 * Retrieves all portal actions.
 */
export const getAllPortalActions = async (
    pageSize,
    page,
    sort,
    order,
    queryParams,
) => {
    const params = new URLSearchParams({
        pageSize,
        page,
        sort,
        order,
    });

    if (queryParams && Object.keys(queryParams).length !== 0) {
        Object.entries(queryParams).forEach(([key, value]) => {
            if (Array.isArray(value)) {
                value.forEach(item => params.append(key, item));
            } else {
                params.append(key, value);
            }
        });
    }

    try {
        const tenantID = localStorage.getItem('tenantId');
        const response = await contentHttp.get(
            `/v2/content/tenant/${tenantID}/actions?${params.toString()}`,
        );
        return response?.data || null;
    } catch (error) {
        console.error('Error fetching actions data:', error);
        throw error;
    }
};

/**
 * Retrieves single portal action.
 */
export const getPortalActionById = async id => {
    try {
        const tenantID = localStorage.getItem('tenantId');
        const response = await contentHttp.get(
            `/v2/content/tenant/${tenantID}/actions/${id}`,
        );
        return response?.data || null;
    } catch (error) {
        console.error('Error fetching action data:', error);
        throw error;
    }
};

/**
 * Retrieves single portal action.
 */
export const getAllResponsesByActionId = async (
    id,
    pageSize,
    page,
    sort,
    order,
    queryParams,
) => {
    let queryString = '';
    if (Object.keys(queryParams).length !== 0) {
        queryString = Object.entries(queryParams)
            .map(([key, value]) => {
                if (Array.isArray(value)) {
                    return value
                        .map(item => `${key}=${encodeURIComponent(item)}`)
                        .join('&');
                }
                return `${key}=${encodeURIComponent(value)}`;
            })
            .join('&');
    }
    try {
        const tenantID = localStorage.getItem('tenantId');
        const response = await contentHttp.get(
            `/v2/content/tenant/${tenantID}/actions/${id}/responses?pageSize=${pageSize}&page=${page}&sort=${sort ?? ''}&order=${order ?? ''}&${queryString}`,
        );
        return response?.data || null;
    } catch (error) {
        console.error('Error fetching responses data:', error);
        throw error;
    }
};
